import { unlockOnGesture } from '@suntex.core/audio';

/**
 * Resume the CreateJS AudioContext on first user interaction.
 */
export function enableAudioUnlock(): void {
  unlockOnGesture(getCreatejsContext);
}

// ─── Sound registration & playback ──────────────────────────────────────────

export function loadSound(src: string, id: string): void {
  createjs.Sound.registerSound(src.replace(/ /g, '%20'), id);
}

export function playSound(id: string): void {
  if (!createjs.Sound.muted) {
    createjs.Sound.play(id);
  }
}

export function playSoundAtVolume(id: string, volume: number): void {
  if (!createjs.Sound.muted) {
    const instance = createjs.Sound.play(id);
    instance.volume = volume;
  }
}

export function mute(): void {
  createjs.Sound.muted = true;
}

export function unmute(): void {
  createjs.Sound.muted = false;
}

/**
 * Returns whether playback is currently muted.
 */
export function isMuted(): boolean {
  return createjs.Sound.muted;
}

// ─── Background music loop ───────────────────────────────────────────────────

const LOOP_ID = '__suntex_loop__';

let _loopSrc: string | null = null;
let _loopStopped = false;
let _loopInstance: createjs.AbstractSoundInstance | null = null;

function _playLoop(): void {
  if (_loopStopped || _loopSrc == null) return;
  _loopInstance = createjs.Sound.play(LOOP_ID);
  _loopInstance.on('complete', () => {
    if (!_loopStopped) _playLoop();
  });
}

/**
 * Load a sound file and start playing it as a continuously looping background
 * track. Call `stopLoop()` to halt and `restartLoop()` to resume.
 */
export function startLoop(src: string): void {
  stopLoop();
  _loopSrc = src;
  _loopStopped = false;
  createjs.Sound.on(
    'fileload',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (e: any) {
      if (
        (e as { id: string }).id === LOOP_ID &&
        !_loopStopped &&
        !_loopInstance
      ) {
        _playLoop();
      }
    },
    undefined,
    true,
  );
  createjs.Sound.registerSound(src.replace(/ /g, '%20'), LOOP_ID);
}

/**
 * Resume the background loop that was paused by `stopLoop()`.
 */
export function restartLoop(): void {
  _loopStopped = false;
  _playLoop();
}

/**
 * Stop the current background loop.
 */
export function stopLoop(): void {
  _loopStopped = true;
  if (_loopInstance) {
    _loopInstance.stop();
    _loopInstance = null;
  }
}

// ─── AudioContext unlock ─────────────────────────────────────────────────────
// CreateJS only unlocks audio on touch devices. This handles desktop Firefox/Safari.

function getCreatejsContext(): AudioContext | null | undefined {
  return createjs.WebAudioPlugin.context;
}
